/**
 * Phrase Test Page
 * Test phrases with different modes
 */
import { useState, useEffect } from 'react';
import { UserProgress } from '../services/storage';
import { allPhrasesData, Phrase } from '../data/phrases-complete';
import { ttsService } from '../services/tts';
import { storageService } from '../services/storage';
import { initMistakeReviewService, mistakeReviewService } from '../services/mistake-review';

interface PhraseTestPageProps {
  user: UserProgress;
  onExit: () => void;
}

type TestMode = 'meaning-to-phrase' | 'phrase-to-meaning' | 'listen-and-speak';

export default function PhraseTestPage({ user, onExit }: PhraseTestPageProps) {
  const [testMode, setTestMode] = useState<TestMode>('meaning-to-phrase');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phrases, setPhrases] = useState<Phrase[]>([]);
  const [score, setScore] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [userAnswer, setUserAnswer] = useState('');
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [options, setOptions] = useState<string[]>([]);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  // Initialize
  useEffect(() => {
    initMistakeReviewService(user.id);
    initTest();
  }, [user.id]);

  // Initialize test with random phrases
  const initTest = () => {
    const shuffled = [...allPhrasesData].sort(() => Math.random() - 0.5).slice(0, 20);
    setPhrases(shuffled);
    setCurrentIndex(0);
    setScore(0);
    setShowAnswer(false);
    setUserAnswer('');
    setIsCorrect(null);
    setSelectedOption(null);

    if (testMode === 'phrase-to-meaning') {
      generateOptions(shuffled[0]);
    }
  };

  // Generate options for multiple choice
  const generateOptions = (phrase: Phrase) => {
    const correctAnswer = phrase.meaning;
    const others = allPhrasesData.filter(p => p.id !== phrase.id);
    const randomOthers = others.sort(() => Math.random() - 0.5).slice(0, 3);
    const wrongAnswers = randomOthers.map(p => p.meaning);
    const allOptions = [correctAnswer, ...wrongAnswers].sort(() => Math.random() - 0.5);
    setOptions(allOptions);
  };

  // Get current phrase
  const currentPhrase = phrases[currentIndex];

  // Handle answer submit
  const handleSubmit = () => {
    if (!currentPhrase) return;

    setShowAnswer(true);

    if (testMode === 'meaning-to-phrase') {
      // User inputs phrase based on meaning
      const correct = userAnswer.toLowerCase().trim() === currentPhrase.phrase.toLowerCase();
      setIsCorrect(correct);

      if (correct) {
        setScore(s => s + 1);
        storageService.addPoints(user.id, 10);
      } else {
        // Record mistake
        if (mistakeReviewService) {
          mistakeReviewService.addMistake(
            'speaking',
            currentPhrase.id,
            currentPhrase.phrase,
            userAnswer.trim(),
            currentPhrase.phrase
          );
        }
      }
    }
  };

  // Handle multiple choice
  const handleOptionSelect = (option: string) => {
    if (showAnswer) return;

    setSelectedOption(option);
    setShowAnswer(true);

    const correct = option === currentPhrase?.meaning;
    setIsCorrect(correct);

    if (correct) {
      setScore(s => s + 1);
      storageService.addPoints(user.id, 5);
    } else {
      // Record mistake
      if (mistakeReviewService && currentPhrase) {
        mistakeReviewService.addMistake(
          'vocabulary',
          currentPhrase.id,
          currentPhrase.phrase,
          option,
          currentPhrase.meaning
        );
      }
    }
  };

  // Next phrase
  const handleNext = () => {
    if (currentIndex < phrases.length - 1) {
      setCurrentIndex(i => i + 1);
      setShowAnswer(false);
      setUserAnswer('');
      setIsCorrect(null);
      setSelectedOption(null);

      if (testMode === 'phrase-to-meaning' && phrases[currentIndex + 1]) {
        generateOptions(phrases[currentIndex + 1]);
      }
    } else {
      // Test complete
      alert(`🎉 测试完成！\n正确率: ${Math.round((score / phrases.length) * 100)}%\n得分: ${score}/${phrases.length}`);
      onExit();
    }
  };

  // Play phrase audio
  const playPhrase = async () => {
    if (currentPhrase) {
      await ttsService.speakNormal(currentPhrase.phrase);
    }
  };

  if (!currentPhrase) {
    return <div className="text-center py-12">加载中...</div>;
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <button onClick={onExit} className="text-gray-500 hover:text-gray-700">
          ← 返回
        </button>
        <div className="text-center">
          <h2 className="text-xl font-bold text-purple-600">📝 短语测试</h2>
          <p className="text-sm text-gray-500">
            第 {currentIndex + 1}/{phrases.length} 题
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm font-bold text-purple-600">得分: {score}</p>
        </div>
      </div>

      {/* Test mode selector */}
      {!showAnswer && currentIndex === 0 && (
        <div className="bg-white rounded-3xl p-6 shadow-soft mb-6">
          <h3 className="font-bold text-gray-700 mb-4">选择测试模式</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button
              onClick={() => {
                setTestMode('meaning-to-phrase');
                setUserAnswer('');
              }}
              className={`p-4 rounded-xl font-medium transition-all ${
                testMode === 'meaning-to-phrase'
                  ? 'bg-purple-100 border-2 border-purple-500 text-purple-700'
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              🎯 看中文说英文
            </button>
            <button
              onClick={() => {
                setTestMode('phrase-to-meaning');
                generateOptions(currentPhrase);
              }}
              className={`p-4 rounded-xl font-medium transition-all ${
                testMode === 'phrase-to-meaning'
                  ? 'bg-purple-100 border-2 border-purple-500 text-purple-700'
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              🎯 看英文选中文
            </button>
          </div>
        </div>
      )}

      {/* Question card */}
      <div className="bg-white rounded-3xl p-8 shadow-soft mb-6">
        {/* Meaning-to-phrase mode */}
        {testMode === 'meaning-to-phrase' && (
          <>
            <div className="text-center mb-6">
              <p className="text-gray-600 mb-2">请说出或输入对应的英文短语：</p>
              <p className="text-2xl font-bold text-gray-800">{currentPhrase.meaning}</p>
            </div>

            <input
              type="text"
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              placeholder="输入英文短语..."
              disabled={showAnswer}
              className="w-full px-4 py-3 text-lg border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:outline-none disabled:opacity-50 mb-4"
              autoFocus
            />

            {!showAnswer && (
              <button
                onClick={handleSubmit}
                disabled={!userAnswer.trim()}
                className="w-full bg-purple-500 text-white py-3 rounded-xl font-bold hover:bg-purple-600 disabled:opacity-50"
              >
                ✓ 提交答案
              </button>
            )}
          </>
        )}

        {/* Phrase-to-meaning mode */}
        {testMode === 'phrase-to-meaning' && (
          <>
            <div className="text-center mb-6">
              <button
                onClick={playPhrase}
                className="text-4xl mb-2 hover:scale-110 transition-transform"
              >
                🔊
              </button>
              <p className="text-gray-600 mb-2">选择正确的中文意思：</p>
              <p className="text-2xl font-bold text-gray-800">{currentPhrase.phrase}</p>
            </div>

            <div className="grid grid-cols-1 gap-3">
              {options.map((option, idx) => (
                <button
                  key={idx}
                  onClick={() => handleOptionSelect(option)}
                  disabled={showAnswer}
                  className={`p-4 rounded-xl text-left font-medium transition-all ${
                    showAnswer
                      ? option === currentPhrase.meaning
                        ? 'bg-green-100 border-2 border-green-500 text-green-700'
                        : selectedOption === option
                        ? 'bg-red-100 border-2 border-red-500 text-red-700'
                        : 'bg-gray-50 text-gray-500'
                      : 'bg-gray-50 hover:bg-purple-50 border-2 border-gray-200 hover:border-purple-400'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </>
        )}

        {/* Show answer */}
        {showAnswer && (
          <div className="mt-6 animate-bounce-in">
            <div className={`p-4 rounded-xl ${isCorrect ? 'bg-green-50' : 'bg-red-50'}`}>
              <p className={`text-lg font-bold ${isCorrect ? 'text-green-600' : 'text-red-600'}`}>
                {isCorrect ? '✅ 正确！' : '❌ 错误'}
              </p>

              {!isCorrect && (
                <div className="mt-2">
                  <p className="text-sm text-gray-600">正确答案：</p>
                  <p className="text-lg font-bold text-gray-800">
                    {testMode === 'meaning-to-phrase' ? currentPhrase.phrase : currentPhrase.meaning}
                  </p>
                </div>
              )}
            </div>

            <button
              onClick={handleNext}
              className="w-full mt-4 bg-purple-500 text-white py-3 rounded-xl font-bold hover:bg-purple-600"
            >
              {currentIndex < phrases.length - 1 ? '下一题 →' : '完成测试'}
            </button>
          </div>
        )}
      </div>

      {/* Progress */}
      <div className="bg-white rounded-xl p-4 shadow-soft">
        <div className="progress-bar">
          <div
            className="progress-bar-fill bg-purple-500"
            style={{ width: `${((currentIndex + 1) / phrases.length) * 100}%` }}
          />
        </div>
        <p className="text-center text-sm text-gray-500 mt-2">
          进度: {currentIndex + 1}/{phrases.length}
        </p>
      </div>
    </div>
  );
}